// Utils
import { isDate, isEmpty, isString } from 'lodash';

// Components
import DatePicker from 'vue2-datepicker';

export default {

    components: { DatePicker },

    inheritAttrs: false,

    props: {
        firstDayOfWeek: {
            type: Number,
            default: 1
        },

        format: {
            type: String,
            default: null
        },

        notAfter: {
            type: [Date, String],
            default: null
        },

        notBefore: {
            type: [Date, String],
            default: null
        },

        value: {
            type: [Date, Array, Number, String],
            default: null
        },

        valueType: {
            type: String,
            default: 'timestamp'
        },

        timezone: {
            type: String,
            default: Intl.DateTimeFormat().resolvedOptions().timeZone
        },

        timePickerOptions: {
            type: [Object, Function],
            default: () => null,
        },

        invalidDates: {
            type: Function,
            default: null
        },

        invalidTimes: {
            type: Function,
            default: null
        },

        onlyUpdateTime: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            dateTimeFormat: null
        };
    },

    watch: {
        format: {
            immediate: true,
            handler(value) {
                this.dateTimeFormat = value || this.$const.DEFAULT_DATE_FORMAT;
            }
        }
    },

    computed: {
        notBeforeDateTime() {
            return this.$utils.time.transformTzTimeToLocal(this.notBefore, this.timezone).toDate();
        },
        /** @returns {any} */
        innerValue: {
            get() {
                let value = this.eventTzToLocalTz(this.value);
                if (['x', 'X'].includes(this.valueType)) {
                    value = value ? value.toString() : undefined;
                }
                return value;
            },

            set(value) {
                if (['x', 'X'].includes(this.valueType)) {
                    value = this.localTzToEventTz(value);
                    value = this.$utils.time.removeTimestampSeconds(value, this.valueType === 'x');
                } else if (this.onlyUpdateTime) {
                    const nextValue = new Date(this.value.getTime());
                    nextValue.setSeconds(0);
                    nextValue.setHours(value.getHours());
                    nextValue.setMinutes(value.getMinutes());
                    this.$emit('input', nextValue);
                    return true;
                }

                this.$emit('input', value);
            }
        },

        /**
         * @returns {Date|null}
         */
        notAfterDate() {
            let date = null;

            if (isDate(this.notAfter)) {
                date = this.$utils.time.transformTzTimeToLocal(this.notAfter, this.timezone).toDate();
            }

            if (!isEmpty(this.notAfter) && isString(this.notAfter)) {
                if (!this.notAfter.includes('T') && !this.notAfter.includes(' ')) {
                    date = new Date(`${this.notAfter}T00:00:00`);
                } else {
                    date = new Date(this.notAfter);
                }
            }

            if (this.onlyUpdateTime && date && this.value.getTime() <= date.getTime()) {
                date = null;
            }

            return date;
        },

        /**
         * @returns {Date|null}
         */
        notBeforeDate() {
            let date = null;

            if (isDate(this.notBefore)) {
                date = this.$utils.time.transformTzTimeToLocal(this.notBefore, this.timezone).toDate();
            }

            if (!isEmpty(this.notBefore) && isString(this.notBefore)) {
                if (!this.notBefore.includes('T') && !this.notBefore.includes(' ')) {
                    date = new Date(`${this.notBefore}T00:00:00`);
                } else {
                    date = new Date(this.notBefore);
                }
            }

            if (this.onlyUpdateTime && date && this.value.getTime() >= date.getTime()) {
                date = null;
            }

            return date;
        },

        /**
         * @returns {Date|null}
         */
        notAfterEndOfDay() {
            if (isDate(this.notAfterDate)) {
                return this.$utils.time.endOfDay(this.notAfterDate);
            }

            return null;
        },

        /**
         * @returns {Date|null}
         */
        notBeforeBeginOfDay() {
            if (isDate(this.notBeforeDate)) {
                return this.$utils.time.startOfDay(this.notBeforeDate);
            }

            return null;
        },

        /**
         * Pass-through listeners
         * @returns {object}
         */
        listeners() {
            // eslint-disable-next-line
            const { input, change, ...listeners } = this.$listeners;

            return listeners;
        },

        /**
         * Pass-through attributes
         * @returns {object}
         */
        attrs() {

            /* eslint-disable no-unused-vars */
            const {
                label,
                hint,
                focused,
                value,
                legacy,
                statuses,
                errors,
                ...attrs
            } = Object.assign(this.$options.propsData, this.$attrs);
            /* eslint-enable no-unused-vars */

            return attrs;
        }
    },

    methods: {
        /**
         * Checks if the given date is within
         * the before and after range
         *
         * @param {Date} date the date to check against the given range
         *
         * @returns {Boolean} whether the given date is disabled or not
         */
        disabledDate(date) {
            if (typeof this.invalidDates === 'function') {
                return this.invalidDates(date);
            }

            const notBefore = this.notBeforeBeginOfDay;
            const notAfter = this.notAfterEndOfDay;
            const { dateIsBefore, dateIsAfter } = this.$utils.time;

            let isBefore, isAfter = false;

            if (isDate(notBefore)) isBefore = dateIsBefore(date, notBefore, this.timezone);
            if (isDate(notAfter)) isAfter = dateIsAfter(date, notAfter, this.timezone);

            return isBefore || isAfter;
        },

        /**
         * Checks if the given time is within
         * the before and after range
         *
         * @param {Date} time the time to check against the given range
         *
         * @returns {Boolean} whether the given time is disabled or not
         */
        disabledTime(time) {
            if (typeof this.invalidTimes === 'function') {
                return this.invalidTimes(time);
            }
            // computed notBeforeDateTime - keeps the selectable time options as disabled when they're supposed to be disabled.
            // Otherwise they switch to active when we selected late enough time (on the same, current day).
            const notBefore = this.notBeforeDateTime;
            const notAfter = this.notAfterDate;
            const { dateIsBefore, dateIsAfter, timeDiff } = this.$utils.time;
            let changed;

            if (this.clonedTime && this.clonedTime.getTime() !== time.getTime()) {
                const diff = timeDiff(time, this.clonedTime, 'minutes');
                changed = diff < 60 ? 'minute' : 'hours';
            }

            this.clonedTime = new Date(time.getTime()); // Clone date in order not to mess with plugin references

            let isBefore = false;
            let isAfter = false;

            if (isDate(notBefore)) {
                // Floor insignificant (for the view) measure down
                notBefore.setSeconds(0);
                notBefore.setMilliseconds(0);

                if (changed === 'hours') {
                    // This will be true only if we're evaluating whether to
                    // enable a specific *Hour* (not minute) in the picker
                    // and the user did not provided a default value on the
                    // input date picker

                    // When no default value is given, but not-before or
                    // not-after values are set, the date picker will call
                    // this callback with a date set with
                    // time: X:00 00.000, but we are comparing with a X:35
                    // (for example), resulting with a positive `isBefore`
                    // check, disabling the hour picker.

                    // To prevent this to happen, we artificially set the minutes
                    // of the given date to the same value of the `not-before`
                    // and `not-after` so that the date comparison could return
                    // a valid comparison check.
                    if (notBefore.getHours() === this.clonedTime.getHours()) {
                        this.clonedTime.setMinutes(notBefore.getMinutes());
                    }
                }

                isBefore = dateIsBefore(this.clonedTime, notBefore, this.timezone);
            }

            if (isDate(notAfter)) {
                // Ceil insignificant (for the view) measure up
                notAfter.setSeconds(59);
                notAfter.setMilliseconds(59);

                if (changed === 'hours') {
                    if (notAfter.getHours() === this.clonedTime.getHours()) {
                        this.clonedTime.setMinutes(notAfter.getMinutes());
                    }
                }

                isAfter = dateIsAfter(this.clonedTime, notAfter, this.timezone);
            }

            return isBefore || isAfter;
        },

        localTzToEventTz(value) {
            return this.convertTimezone(value, 'tz');
        },

        eventTzToLocalTz(value) {
            return this.convertTimezone(value, 'local');
        },

        convertTimezone(value, direction = 'local') {
            if (['x', 'X'].includes(this.valueType)) {
                if (typeof value === 'string') {
                    value = Number.parseInt(value);
                }

                if (Number.isFinite(value)) {
                    const ts = this.valueType === 'X' ? value * 1000 : value;
                    const util = direction === 'local' ? 'transformTzTimeToLocal' : 'transformLocalTimeToTz';
                    const date = this.$utils.time[util](ts, this.timezone);
                    const valueString = date.format(this.valueType);
                    value = Number.parseInt(valueString);
                }
            }

            return value;
        }

    }
};
